/**
 * Die **Befundzelle**: was an einer Stelle gefunden wurde, in zwei Zeilen.
 *
 * Oben die Zahl in der Farbe des schwersten Grades und der Titel des
 * relevantesten Befundes; unten das Namensband — wer es gesehen hat, und das
 * Zeichen „Regel", wo ein Kontrollmuster beteiligt war.
 *
 * **Zwei Zeilen und nicht drei.** Die Zahl sagt, wie viel hier liegt, der Titel,
 * wovon es handelt — beides gehört in einen Blick, und die Farbe trägt die
 * Schwere, die daneben nicht noch einmal als Wort stehen muss. Wer den Grad in
 * Buchstaben will, findet ihn im Titel der Zahl.
 *
 * **An einer Stelle für beide Listen.** Der Befundvorrat und der Revisionsstrom
 * zeigen denselben Fund; zwei Aufbauten wären zwei Antworten auf dieselbe Frage,
 * sobald einer davon nachgezogen wird und der andere nicht.
 */

import type { ReactElement } from "react";

import { Chip, StatusPill } from "../design-system";
import { formatNumber } from "../formatting";
import type { Severity, SiteAuthor } from "../api/client";
import { AuthorBand } from "./authors";
import { SEVERITY_TONES, UNTITLED, UNTITLED_LABEL } from "./findings";
import { FINDINGS_WIDTH } from "./listWidths";

/** Was von der Spaltenbreite bleibt, wenn die Zellpolsterung der Tabelle abgeht. */
const INSIDE = FINDINGS_WIDTH - 20;

export function FindingCell({
  total,
  severity,
  title,
  authors,
  countTitle,
}: {
  total: number;
  /** `null` heißt: kein Grad bekannt — das Zeichen bleibt dann neutral. */
  severity: Severity | null;
  /** Leer heißt: aus einem Lauf vor der Titelpflicht — dann steht „ohne Titel". */
  title: string;
  authors: SiteAuthor[];
  /** Was im Titel der Zahl steht: die Befundarten oder der schwerste Grad. */
  countTitle?: string | undefined;
}): ReactElement {
  const untitled = title === "";
  // `null` heißt: kein Befund und damit kein Grad. Wie „hinweis" trägt die Zahl
  // dann keine Farbe und steht als `Chip` da — gefärbt läse sie sich wie eine
  // Aussage über eine Schwere, die es hier nicht gibt.
  const tone = severity === null ? null : SEVERITY_TONES[severity];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--sp-3)",
        minWidth: 0,
        maxWidth: INSIDE,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--sp-4)",
          maxWidth: "100%",
        }}
      >
        <span title={countTitle}>
          {tone === null ? (
            <Chip>{formatNumber(total)}</Chip>
          ) : (
            <StatusPill tone={tone}>{formatNumber(total)}</StatusPill>
          )}
        </span>
        {/*
          Abgeschnitten wird der Titel, statt die Spalte zu sprengen; vollständig
          steht er im Titel des Feldes.
        */}
        <span
          title={untitled ? UNTITLED : title}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "var(--fs-12)",
            fontWeight: "var(--fw-medium)",
            color: untitled ? "var(--fg-6)" : "var(--fg-1)",
            fontStyle: untitled ? "italic" : "normal",
          }}
        >
          {untitled ? UNTITLED_LABEL : title}
        </span>
      </span>
      <AuthorBand authors={authors} width={INSIDE} />
    </div>
  );
}
